/**
 * JobTargetCell - Displays a job's target as a primary line plus an optional
 * secondary identifier line.
 *
 * Primary line = `job.dns`, which equals the target's `Group()` segment of the
 * job key. The secondary line shows the target's `Target.Identifier()` value,
 * derived per target type: asset → `name`, webapplication → `primary_url`,
 * preseed → `value`. For computed identifiers (e.g. Webpage.UrlPath()) and
 * unknown/legacy target types there is no single serialized field, so we fall
 * back to the key's identifier segment, which the backend wrote authoritatively
 * (`#job#<Group()>#<Identifier()>#<capability>`, job.go:170-176; see also
 * target.go for the per-type Identifier() definitions).
 *
 * Deliberately excluded: the `system` sentinel from NewSystemJob
 * (`#job#<id>#system#<source>`), and `entity.source` provenance (e.g.
 * "self"/"hannibal" — not an address). The secondary line is suppressed when it
 * equals `job.dns` so we never duplicate the primary line.
 */

import { memo } from 'react';

import { NameColumn } from '@/components/NameColumn';
import { Job } from '@/types';

// Mirrors the backend Target.Identifier() value per target type. The job key is
// stamped `#job#<Group()>#<Identifier()>#<capability>` (job.go:170-176) and
// `job.dns == Group()`, so the secondary line should show Identifier(): for an
// Asset that is `name`, for a WebApplication `primary_url`, for a Preseed
// `value`. For computed identifiers (e.g. Webpage.UrlPath()) and unknown/legacy
// target types there is no single serialized field, so we fall back to the
// key's identifier segment (which the backend wrote authoritatively). The
// `system` sentinel from NewSystemJob (`#job#<id>#system#<source>`) is dropped.
function getTargetIdentifier(job: Job): string | undefined {
  const entity = job.target?.model ?? job.target?.Target;
  switch (job.target?.type?.toLowerCase()) {
    case 'asset':
      return entity?.name;
    case 'webapplication':
      return entity?.primary_url;
    case 'preseed':
      return entity?.value;
    default:
      return identifierFromKey(job.key) ?? entity?.name;
  }
}

function identifierFromKey(key: string | undefined): string | undefined {
  if (!key?.startsWith('#job#')) return undefined;
  // segments: ['', 'job', <dns>, <identifier>, <capability>, (<uuid> if Uniquify'd)]
  const identifier = key.split('#')[3];
  if (!identifier || identifier === 'system') return undefined;
  return identifier;
}

export const JobTargetCell = memo(({ job }: { job: Job }) => {
  const name = job.dns || '-';
  const identifier = getTargetIdentifier(job);
  const description = identifier && identifier !== job.dns ? identifier : '';

  return <NameColumn name={name} description={description} />;
});

JobTargetCell.displayName = 'JobTargetCell';
